import React, { useState } from "react";
import { Heart, Reply, Trash2 } from "lucide-react";
import { Button } from "./ui/button";

export default function MessageBubble({ 
  message, 
  currentUserId, 
  isMasterUser,
  onReaction, 
  onDelete, 
  onReply,
  onImageClick,
  onVideoClick
}) {
  const [showReactions, setShowReactions] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const isOwnMessage = message.sender_id === currentUserId;
  const canDelete = isOwnMessage || isMasterUser;

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  const reactions = ["❤️", "👍", "😂", "😮", "😢", "🙏"];

  const handleMessageClick = () => {
    setShowActions(!showActions);
  };

  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-3`}>
      <div 
        className={`max-w-[75%] flex ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'} items-start gap-2 ${
          message._animateIn ? 'message-slide-in' : ''
        }`}
      >
        {/* Message bubble container */}
        <div className="flex flex-col">
          {/* Sender name (only for others' messages) */}
          {!isOwnMessage && (
            <div className="px-3 mb-1">
              <span className="text-xs font-semibold text-golf-green">{message.sender_name}</span>
            </div>
          )}
        
          <div 
            onClick={handleMessageClick}
            className="relative cursor-pointer"
          >
            {/* Message bubble */}
            <div className={`rounded-2xl px-4 py-2 shadow-sm ${
              isOwnMessage 
                ? 'bg-golf-green text-white rounded-br-md' 
                : 'bg-white text-gray-800 rounded-bl-md border border-gray-100'
            }`}>
              {/* Reply to preview */}
              {message.reply_to && (
                <div className={`text-xs p-2 rounded-lg mb-2 border-l-2 ${
                  isOwnMessage 
                    ? 'bg-white/20 border-white/50' 
                    : 'bg-gray-50 border-golf-green'
                }`}>
                  <p className={`font-medium ${isOwnMessage ? 'text-white/90' : 'text-golf-green'}`}>
                    {message.reply_to.sender_name}
                  </p>
                  <p className={`truncate ${isOwnMessage ? 'text-white/70' : 'text-gray-600'}`}>
                    {message.reply_to.content}
                  </p>
                </div>
              )}

              {/* Media */}
              {message.image_url && (
                <img
                  src={message.image_url}
                  alt="Attached"
                  className="rounded-lg mb-2 cursor-pointer max-w-xs hover:opacity-90 transition-opacity"
                  onClick={() => onImageClick?.(message.image_url)}
                />
              )}
              {message.media_url && (
                <video
                  src={message.media_url}
                  controls
                  className="rounded-lg mb-2 max-w-xs"
                />
              )}

              {/* Message content */}
              {message.content && (
                <p className="whitespace-pre-wrap break-words leading-relaxed">{message.content}</p>
              )}

              {/* Time */}
              <div className={`flex items-center justify-end gap-1 mt-1`}>
                <span className={`text-[10px] ${
                  isOwnMessage ? 'text-white/70' : 'text-gray-500'
                }`}>
                  {formatTime(message.created_at)}
                </span>
              </div>

              {/* Reactions display */}
              {message.reactions && Object.keys(message.reactions).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-white/10">
                  {Object.entries(message.reactions).map(([emoji, users]) => (
                    users.length > 0 && (
                      <button
                        key={emoji}
                        onClick={() => onReaction(message.message_id, emoji)}
                        className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 transition-all ${
                          users.includes(currentUserId)
                            ? isOwnMessage
                              ? 'bg-white/30 ring-1 ring-white/50'
                              : 'bg-golf-green/20 ring-1 ring-golf-green'
                            : isOwnMessage 
                              ? 'bg-white/10 hover:bg-white/20'
                              : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        <span>{emoji}</span>
                        <span className="text-[10px] font-medium">{users.length}</span>
                      </button>
                    )
                  ))}
                </div>
              )}
            </div>

            {/* Reaction picker - правильное позиционирование */}
            {showReactions && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowReactions(false)}
                />
                <div className={`absolute ${isOwnMessage ? 'right-0' : 'left-0'} -top-14 bg-white rounded-2xl shadow-xl p-2 flex gap-1 z-50 border border-gray-200`}>
                  {reactions.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => {
                        onReaction(message.message_id, emoji);
                        setShowReactions(false);
                      }}
                      className="w-10 h-10 hover:bg-gray-100 rounded-xl flex items-center justify-center transition-all hover:scale-110 active:scale-95"
                    >
                      <span className="text-xl">{emoji}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Actions menu - три кнопки справа от сообщения (для всех) */}
        {showActions && (
          <>
            <div 
              className="fixed inset-0 z-30" 
              onClick={() => setShowActions(false)}
            />
            <div className="flex gap-1.5 z-40">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowReactions(!showReactions);
                  setShowActions(false);
                }}
                className="w-7 h-7 bg-white border border-gray-300 hover:bg-gray-100 rounded-full flex items-center justify-center transition-colors shadow-sm"
              >
                <Heart className="w-4 h-4 text-gray-700" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReply?.(message);
                  setShowActions(false);
                }}
                className="w-7 h-7 bg-white border border-gray-300 hover:bg-gray-100 rounded-full flex items-center justify-center transition-colors shadow-sm"
              >
                <Reply className="w-4 h-4 text-gray-700" />
              </button>
              {canDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.(message.message_id);
                    setShowActions(false);
                  }}
                  className="w-7 h-7 bg-white border border-gray-300 hover:bg-gray-100 rounded-full flex items-center justify-center transition-colors shadow-sm"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
